import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import cmsApi from '../../services/cmsApi';
import { getImageUrl, FALLBACK_IMAGE_SVG } from '../../utils/getImageUrl';

const CampusNews = () => {
  const [newsData, setNewsData] = useState({
    title: 'Latest News',
    readMoreButtonText: 'READ MORE',
    news: [] // Start empty, will be populated from API
  });

  // Fetch news content from CMS and News API (client-side only)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const fetchNewsContent = async () => {
      try {
        // Fetch CMS settings
        const cmsResult = await cmsApi.getHomepageComponentByType('campus-news');
        let cmsSettings = {
          title: 'Latest News',
          readMoreButtonText: 'READ MORE',
          newsLimit: 3
        };
        if (cmsResult.success && cmsResult.components.length > 0) {
          const component = cmsResult.components[0];
          const settings = component.settings || {};
          cmsSettings = {
            title: component.title || 'Latest News',
            readMoreButtonText: settings.readMoreButtonText || 'READ MORE',
            newsLimit: settings.newsLimit || 3
          };
        }

        // Fetch news from API - exclude breaking news for Campus News section
        try {
          // Get news items, excluding breaking news
          // Campus News should show regular published news, not breaking news
          const newsResult = await cmsApi.getNews({ 
            limit: 10, // Get more to filter
            breaking: 'false' // Explicitly exclude breaking news
          });
          
          if (newsResult.success && newsResult.news && newsResult.news.length > 0) {
            // Filter out breaking news items and get configured number
            const newsLimit = cmsSettings.newsLimit || 3;
            const campusNews = newsResult.news
              .filter(article => {
                // Exclude breaking news (priority: breaking or isBreaking: true)
                const isBreaking = article.priority === 'breaking' || article.isBreaking === true;
                // Exclude "Cybersecurity Bootcamp" article (should be in Breaking News, not Campus News)
                const isCybersecurityBootcamp = article.slug === 'cybersecurity-bootcamp-final-call' || 
                                               article.title?.toLowerCase().includes('cybersecurity bootcamp');
                return !isBreaking && !isCybersecurityBootcamp;
              })
              .slice(0, newsLimit)
              .map(article => ({
                id: article.id,
                title: article.title,
                description: article.description || article.excerpt || '',
                image: article.image || article.featuredImage || ''
              }));
            
            if (campusNews.length > 0) {
              setNewsData({
                ...cmsSettings,
                news: campusNews
              });
              return;
            }
          }
        } catch (apiError) {
          console.error('News API error:', apiError);
        }

        // No fallback - database is the single source of truth
        // If no news found, show empty state
        if (!newsData.news || newsData.news.length === 0) {
          setNewsData({
            ...cmsSettings,
            news: []
          });
        }
      } catch (error) {
        console.error('Error fetching campus news:', error);
        // No hardcoded fallback - database is the source of truth
        setNewsData({
          title: 'Latest News',
          readMoreButtonText: 'READ MORE',
          news: []
        });
      }
    };

    fetchNewsContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const news = newsData.news;

  return (
    <section className="bg-gray-100 py-8 sm:py-12 md:py-16 px-4 sm:px-6 md:px-8 text-center min-[1024px]:max-[1535px]:!py-10">
      {/* News Container */}
      <div className="container mx-auto p-4 sm:p-6 md:p-8 rounded-lg min-[1024px]:max-[1535px]:!p-5">
        <h2 className="text-2xl sm:text-3xl font-bold text-left text-custom-pink mb-6 sm:mb-8 min-[1024px]:max-[1535px]:!text-2xl min-[1024px]:max-[1535px]:!mb-5">
          {newsData.title}
        </h2>

        {/* News Cards */}
        <div className="flex flex-col sm:flex-row flex-wrap sm:flex-nowrap gap-6 sm:gap-8 min-[1024px]:max-[1535px]:!gap-5">
          {news.map((newsItem, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg overflow-hidden w-full sm:w-[calc(50%-1rem)] md:w-[calc(33.333%-1rem)] relative flex flex-col"
            >
              {/* News Image */}
              <div className="relative">
                <img
                  src={getImageUrl(newsItem.image)}
                  alt={newsItem.title}
                  className="w-full h-[200px] sm:h-[240px] md:h-[270px] object-cover"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMAGE_SVG;
                  }}
                />
                <span className="block border-b-4 border-gold w-full"></span>
              </div>

              {/* News Content */}
              <div className="flex-grow p-4 sm:p-6 md:p-8 flex text-left flex-col justify-between min-[1024px]:max-[1535px]:!p-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-black mb-3 sm:mb-4 min-[1024px]:max-[1535px]:!text-base">
                    {newsItem.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 min-[1024px]:max-[1535px]:!text-sm">
                    {newsItem.description}
                  </p>
                </div>

                {/* Read More Button */}
                <Link
                  to={`/news/${newsItem.id}`}
                  className="bg-custom-pink py-3 sm:py-4 px-6 sm:px-8 rounded-full text-white text-sm sm:text-base border border-transparent hover:bg-transparent hover:text-custom-pink hover:border-custom-pink transition duration-300 self-start inline-block min-[1024px]:max-[1535px]:!py-2 min-[1024px]:max-[1535px]:!px-5 min-[1024px]:max-[1535px]:!text-sm"
                >
                  {newsData.readMoreButtonText}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/*     <span className="block border-dotted border-l-4 border-gold mb-4 mt-20 w-28 h-24 mx-auto md:ml-auto md:mr-0 lg:ml-[925px] bg-gray-100"></span>*/}
    </section>
  );
};

export default CampusNews;
